import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { StockModel } from "../../Api/Models/stock.model";
import { stockService } from "../../Api/Services/StockService";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { useLoading } from "../../Context/LoadingProvider";
import StockOrder from "./StockOrder";
import StockManagementNavigation from "../../Components/StockManagementNavigation";
import { PlusIcon } from "../../Components/Icons/PlusIcon";
import { CloseIcon } from "../../Components/Icons/CloseIcon";
import { SearchIcon } from "../../Components/Icons/SearchIcon";
import { HomeIcon } from "../../Components/Icons/HomeIcon";


interface StockListProps {}

type GroupedVariation = {
    variationName: string;
    new: StockModel | null;
    reused: StockModel | null;
};


const StockList: React.FunctionComponent<StockListProps> = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [stocks, setStocks] = useState<StockModel[]>([]);
  const { startLoading, finnishLoading } = useLoading();
  const [stockCorrectionProductId, setStockCorrectionProductId] = useState<
    number | null
  >(null);
  const { showInfo } = useInfoModal();

  const stockCorrectionTextRef = useRef<HTMLTextAreaElement | null>(null);
  const stockCorrectionNumberRef = useRef<HTMLInputElement | null>(null);
  const [selectedProductVariation, setSelectedProductVariation] = useState<
    number | null
  >(null);

  const [selectedStockItems, setSelectedStockItems] = useState<StockModel[]>(
    []
  );

  const [showStockItem, setShowStockItem] = useState<StockModel | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockCorrectionType, setStockCorrectionType] = useState<"new" | "reused" | null>(null);
  const [showCorrectionTypeSelector, setShowCorrectionTypeSelector] = useState<any>(null);


    useEffect(() => {
    loadStocks();
    //eslint-disable-next-line
  }, []);

  const loadStocks = () => {
    startLoading("get-stock-list");
    stockService
      .listStocks()
      .then((stocks) => {
        setStocks(stocks);
      })
      .finally(() => {
        finnishLoading("get-stock-list");
      });
  };

  const sendStockCorrectionRequest = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      stockCorrectionProductId &&
      stockCorrectionTextRef.current &&
      stockCorrectionNumberRef.current
    ) {
      startLoading("send-stock-correction");

      stockService
        .stockCorrectionRequest(
          stockCorrectionProductId,
          selectedProductVariation,
          +stockCorrectionNumberRef.current.value,
          stockCorrectionTextRef.current?.value
        )
        .then((stocks) => {
          setStockCorrectionProductId(null);
          if (
            stockCorrectionTextRef.current &&
            stockCorrectionNumberRef.current
          ) {
            stockCorrectionTextRef.current.value = "";
            stockCorrectionNumberRef.current.value = "";
          }
          setSelectedProductVariation(null);
          showInfo(t("Stock correction request sent!"));
        })
        .finally(() => {
          finnishLoading("send-stock-correction");
        });
    }
  };

  const closeStockOrder = () => {
    setSelectedStockItems([]);
  };

    const uniqueProductStocks = (): StockModel[] => {
        let uniqueProductStocks: StockModel[] = [];

        stocks.forEach((stock) => {
            if (stock.originalProductId !== null) {
                return;
            }

            if (!uniqueProductStocks.some((x) => x.productId === stock.productId)) {
                uniqueProductStocks.push(stock);
            }
        });

        return uniqueProductStocks;
    };

  const filteredStocks = (): StockModel[] => {
    return uniqueProductStocks()
        .filter((stock) =>
            (lang === "ar" ? stock.productNameAr : stock.productName)
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
          const aQuantity = stocks
              .filter((x) => x.productId === a.productId)
              .reduce((sum, obj) => sum + obj.quantity + obj.sanitizedQuantity, 0);

          const bQuantity = stocks
              .filter((x) => x.productId === b.productId)
              .reduce((sum, obj) => sum + obj.quantity + obj.sanitizedQuantity, 0);

          return bQuantity - aQuantity;
        });
  };

    const groupedVariations: GroupedVariation[] = (() => {
        if (!showStockItem) return [];

        const all = stocks.filter(
            (x) =>
                x.productId === showStockItem.productId ||
                x.originalProductId === showStockItem.productId
        );

        const grouped: Record<string, GroupedVariation> = {};

        all.forEach((item) => {
            const key = item.variationName ?? String(item.variationId);

            if (!grouped[key]) {
                grouped[key] = {
                    variationName: key,
                    new: null,
                    reused: null,
                };
            }

            if (item.originalProductId === null) {
                grouped[key].new = item;
            } else {
                grouped[key].reused = item;
            }
        });

        return Object.values(grouped);
    })();





    if (selectedStockItems.length > 0) {
    return <StockOrder stocks={selectedStockItems} close={closeStockOrder} />;
  }

  return (
    <div className="view-container background-white">
      <div className="view-content">

        <div className="navigationbar navigationbar--dark mb-6">
          <div className="navigationbar__left">
            <Link to="/menu" className="navigationbar__button">
              <HomeIcon/>
            </Link>
          </div>
          <div className="navigationbar__center">
            <div className="navigationbar__title">{t("Stock Management")}</div>
          </div>
          <div className="navigationbar__right">
          </div>
        </div>

        <StockManagementNavigation selectedIndex={0}/>

        <div className="mb-2 mt-4">
          <label className="relative">
            <div className="icon-button icon-button--purple icon-button--square input-icon--right">
              <SearchIcon/>
            </div>

            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field input-field--with-icon"
                placeholder={t("Search") ?? ""}
            />
          </label>
        </div>

        <div className="">

          <div className="card-list mt-4">
            {filteredStocks().map((stock, index) => (
                <div className="stock-list-card m" key={index}>
                  <div className="stock-list-card__top">

                    <div className="flex gap-2 justify-between items-center">
                      <div className="flex gap-2">
                        <div className="stock-list-card__image">
                          {stock.productImage && (
                              <img src={stock.productImage} height="50px" alt="Product"/>
                          )}
                        </div>

                        <div className="flex items-center">
                          <div
                              className="stock-list-card__title"
                              
                              onClick={() => {
                                setShowStockItem(stock);
                              }}
                          >
                            {lang === "ar" ? stock.productNameAr : stock.productName}
                            <div className="text-secondary">{stock.barcode}</div>
                          </div>
                        </div>

                      </div>

                      {stock.orderable ? (
                          <button
                              className="button-add button-add--purple"
                              onClick={() => {
                                const selectedStocks = stock.variationId
                                    ? stocks.filter(
                                        (x) => x.productId === stock.productId
                                    )
                                    : [stock];

                                setSelectedStockItems(selectedStocks);
                              }}
                          >
                            <PlusIcon/>
                          </button>
                      ) : null}
                    </div>

                      {stock.variationId && (
                          <>
                              <div className="mb-1">
                                  <div className="stock-list-card__variation-infos">
                                      NEW:
                                      {stocks
                                          .filter(
                                              (x) =>
                                                  x.productId === stock.productId &&
                                                  x.originalProductId === null
                                          )
                                          .sort((a, b) => (a.short_id ?? 9999) - (b.short_id ?? 9999))
                                          .map((stockVariation) => (
                                              <div
                                                  className={
                                                      "variation-pcs-info " +
                                                      ((stockVariation.quantity + stockVariation.sanitizedQuantity) === 0
                                                          ? "variation-pcs-info--disabled"
                                                          : "")
                                                  }
                                                  key={stockVariation.variationId}
                                              >
                                                  <div className="variation-pcs-info__size">
                                                      {stockVariation.variationName}
                                                  </div>

                                                  <div className="variation-pcs-info__quantity">
                                                      {stockVariation.quantity + stockVariation.sanitizedQuantity}
                                                  </div>
                                              </div>
                                          ))}
                                  </div>
                              </div>

                              {(() => {
                                  const reusedList = stocks.filter(
                                      (x) => x.originalProductId === stock.productId
                                  );

                                  return reusedList.length > 0 ? (
                                      <div>
                                          <div>
                                              <div className="stock-list-card__variation-infos">
                                                  REUSED:
                                                  {reusedList
                                                      .sort(
                                                          (a, b) =>
                                                              (a.short_id ?? 9999) - (b.short_id ?? 9999)
                                                      )
                                                      .map((stockVariation) => (
                                                          <div
                                                              className={
                                                                  "variation-pcs-info " +
                                                                  ((stockVariation.quantity + stockVariation.sanitizedQuantity) === 0
                                                                      ? "variation-pcs-info--disabled"
                                                                      : "")
                                                              }
                                                              key={stockVariation.variationId}
                                                          >
                                                              <div className="variation-pcs-info__size">
                                                                  {stockVariation.variationName}
                                                              </div>

                                                              <div className="variation-pcs-info__quantity">
                                                                  {stockVariation.quantity + stockVariation.sanitizedQuantity}
                                                              </div>
                                                          </div>
                                                      ))}
                                              </div>
                                          </div>
                                      </div>
                                  ) : null;
                              })()}
                          </>
                      )}






                      <div className="separator-line separator-line--p10"></div>

                    <div className="flex justify-between gap-2">
                      <div className="flex gap-1 items-center">
                          <div className="stock-list-card__pc">
                              {(() => {
                                  const newQty = stocks
                                      .filter((x) => x.productId === stock.productId && x.originalProductId === null)
                                      .reduce((sum, obj) => sum + obj.quantity + obj.sanitizedQuantity, 0);

                                  const reusedQty = stocks
                                      .filter((x) => x.originalProductId === stock.productId)
                                      .reduce((sum, obj) => sum + obj.quantity + obj.sanitizedQuantity, 0);

                                  return newQty + reusedQty;
                              })()}{" "}
                              {t("pc")}
                          </div>

                      </div>
                      <div>
                        <p
                            className="link"
                            onClick={() => {

                                const hasReused = stocks.some(
                                    x => x.originalProductId === stock.productId
                                );

                                if (!hasReused) {
                                    setStockCorrectionProductId(stock.productId);
                                    setStockCorrectionType("new");
                                    return;
                                }

                                setShowCorrectionTypeSelector({
                                    product: stock,
                                });
                            }}
                        >
                          {t("Stock correction")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
            ))}

          </div>
        </div>
      </div>

      <div
          className={"modal" + (!stockCorrectionProductId ? " hidden" : "")}
          tabIndex={-1}
          aria-modal="true"
          role="dialog"
      >
        <div className="modal__dialog modal-dialog--full-height">
          <div className="modal__header">
              <div className="modal__heading">
                  {stockCorrectionType === "reused"
                      ? t("Reused stock correction")
                      : t("New stock correction")}
              </div>
            <div
              className="modal__close"
              onClick={() => {
                setStockCorrectionProductId(null);
                if (stockCorrectionTextRef.current) {
                  stockCorrectionTextRef.current.value = "";
                }
              }}
            >
              ✖
            </div>
          </div>
          <form className="modal__body" onSubmit={sendStockCorrectionRequest}>
            {stocks.filter((x) => x.productId === stockCorrectionProductId)
              .length > 1 ? (
              <>
                <label className="text-secondary">{t("Size")}</label>
                <select
                  className="input-field bg-lightgray mb-2"
                  onChange={(e) => setSelectedProductVariation(+e.target.value)}
                  required
                >
                  <option value="">-</option>
                  {stocks
                    .filter((x) => x.productId === stockCorrectionProductId)
                    .map((stock) => (
                      <option
                        key={stock.variationId}
                        value={stock.variationId ?? ""}
                      >
                        {stock.variationName}
                      </option>
                    ))}
                </select>
              </>
            ) : null}
            <label className="text-secondary">{t("Updated quantity")}</label>
            <input
              type="number"
              step={1}
              min={0}
              max={9999999}
              ref={stockCorrectionNumberRef}
              placeholder={t("Updated quantity") ?? ""}
              className="input-field mb-2"
              required
            />
            <label className="text-secondary">{t("Message")}</label>
            <textarea
              ref={stockCorrectionTextRef}
              placeholder={t("Message") ?? ""}
              className="input-field mb-6"
            ></textarea>
            <div className="flex justify-centermt-5">
              <button type="submit" className="button">
                {t("Send")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div
        className={"bottom-modal" + (!showStockItem ? " hidden" : "")}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
      >
          <div className="bottom-modal__scroll-content">
            <div className="bottom-modal__body" style={{ marginTop: "50px" }}>

            <div className="stock-item__image">
              <img
                src={showStockItem?.productImage ?? ""}
                style={{ height: "60px", width: "auto" }}
                alt="Product"
              />
            </div>  

            <div className="flex justify-end mb-4">
              <div
                className="bottom-modal__close"
                onClick={() => {
                  setShowStockItem(null);
                }}
              >
                <CloseIcon />
              </div>
            </div>

            <div className="stock-item__title heading text-center mb-6">
              {showStockItem?.productName}
            </div>

                {groupedVariations.map((v) => {
                    const cleanName =
                        v.variationName && v.variationName !== "null"
                            ? v.variationName
                            : null;

                    return (
                        <div className="stock-item__stock-info mb-4" key={v.variationName}>

                            {cleanName && (
                                <div className="order-product-variation__variation-name">
                                    {cleanName}
                                </div>
                            )}

                            <div>
                                <div className="flex items-center gap-1 mt-2">
                                    {v.new && (
                                        <div className="flex items-center gap-1">
                                            <div className="stock-item__stock-type">{t("New")}</div>
                                            <div className="stock-item__quantity">
                                                {v.new.quantity} {t("pcs")}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-1 mt-2">
                                    {v.reused && (
                                        <div className="flex items-center gap-1">
                                            <div className="stock-item__stock-type">{t("Reused")}</div>
                                            <div className="stock-item__quantity">
                                                {v.reused.quantity} {t("pcs")}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {v.new && v.new.whStock !== 999999999 && (
                                    <div className="flex items-center gap-1 mt-2">
                                        <div className="stock-item__stock-type">
                                            {t("Central Warehouse stock")}
                                        </div>
                                        <div className="stock-item__quantity">
                                            {v.new.whStock} {t("pcs")}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}



            </div>
          </div>
        </div>
        {showCorrectionTypeSelector && (
            <div className="modal">
                <div className="modal__dialog">
                    <div className="modal__header">
                        <div className="modal__heading">{t("Choose stock type")}</div>
                        <div
                            className="modal__close"
                            onClick={() => setShowCorrectionTypeSelector(null)}
                        >
                            ✖
                        </div>
                    </div>

                    <div className="modal__body">
                        <button
                            className="button mb-1"
                            onClick={() => {
                                setStockCorrectionProductId(showCorrectionTypeSelector.product.productId);
                                setStockCorrectionType("new");
                                setShowCorrectionTypeSelector(null);
                            }}
                        >
                            NEW stock
                        </button>

                        <button
                            className="button"
                            onClick={() => {
                                const reusedItem = stocks.find(
                                    x => x.originalProductId === showCorrectionTypeSelector.product.productId
                                );

                                if (reusedItem) {
                                    setStockCorrectionProductId(reusedItem.productId);
                                    setStockCorrectionType("reused");
                                }

                                setShowCorrectionTypeSelector(null);
                            }}
                        >
                            REUSED stock
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default StockList;
